import { Group, Badge, ActionIcon, Button, Text } from "@mantine/core";
import { IconX } from "@tabler/icons-react";
import { useCallback, useMemo } from "react";
import { ErrorReportFilters } from "../../services/workflows.service";
import { formatDateOnlyForDisplay, parseDateOnly } from "../../utils/dateOnly";

interface ErrorReportActiveFiltersProps {
  filters: ErrorReportFilters;
  onFiltersChange: (filters: ErrorReportFilters) => void;
  onClearAll: () => void;
}

interface ActiveFilter {
  key: keyof ErrorReportFilters;
  label: string;
  value: string;
  color?: string;
  itemId?: string; // For array filters
}

const getCategoryColor = (category: string) => {
  switch (category?.toLowerCase()) {
    case "accessioning":
      return "blue";
    case "grossing":
      return "grape";
    case "processing":
      return "cyan";
    default:
      return "gray";
  }
};

export default function ErrorReportActiveFilters({
  filters,
  onFiltersChange,
  onClearAll,
}: ErrorReportActiveFiltersProps) {
  const activeFilters = useMemo(() => {
    const nextFilters: ActiveFilter[] = [];

    if (filters.categories?.length) {
      filters.categories.forEach((category) => {
        nextFilters.push({
          key: "categories",
          label: "Category",
          value: category.charAt(0).toUpperCase() + category.slice(1),
          color: getCategoryColor(category),
          itemId: category,
        });
      });
    }

    if (filters.blocking) {
      nextFilters.push({
        key: "blocking",
        label: "Blocking",
        value:
          filters.blocking === "blocking"
            ? "Blocking Only"
            : "Non-blocking Only",
        color: "orange",
      });
    }

    if (filters.fromDate) {
      const fromDate = parseDateOnly(filters.fromDate);
      nextFilters.push({
        key: "fromDate",
        label: "From",
        value: fromDate ? formatDateOnlyForDisplay(fromDate) : "Invalid date",
      });
    }

    if (filters.toDate) {
      const toDate = parseDateOnly(filters.toDate);
      nextFilters.push({
        key: "toDate",
        label: "To",
        value: toDate ? formatDateOnlyForDisplay(toDate) : "Invalid date",
      });
    }

    if (filters.search) {
      nextFilters.push({
        key: "search",
        label: "Search",
        value: filters.search,
      });
    }

    return nextFilters;
  }, [filters]);

  const handleRemoveFilter = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      const key = event.currentTarget.dataset.filterKey as
        | keyof ErrorReportFilters
        | undefined;
      const itemId = event.currentTarget.dataset.itemId;
      if (!key) return;

      if (itemId && key === "categories") {
        const currentArray = filters.categories || [];
        const newArray = currentArray.filter((id) => id !== itemId);
        onFiltersChange({
          ...filters,
          categories: newArray.length > 0 ? newArray : undefined,
        });
        return;
      }

      onFiltersChange({
        ...filters,
        [key]: undefined,
      });
    },
    [filters, onFiltersChange]
  );

  if (activeFilters.length === 0) {
    return null;
  }

  return (
    <Group gap="xs" align="center" wrap="wrap">
      <Text size="sm" fw={500} c="dimmed">
        Active:
      </Text>
      {activeFilters.map((filter, index) => (
        <Badge
          key={`${filter.key}-${filter.itemId || index}`}
          variant="light"
          color={filter.color}
          size="lg"
          pr={3}
          rightSection={
            <ActionIcon
              size="xs"
              color={filter.color || "blue"}
              radius="xl"
              variant="transparent"
              data-filter-key={filter.key}
              data-item-id={filter.itemId}
              onClick={handleRemoveFilter}
              aria-label={`Remove ${filter.label} filter`}
            >
              <IconX size={12} />
            </ActionIcon>
          }
        >
          {filter.label}: {filter.value}
        </Badge>
      ))}
      <Button variant="subtle" size="compact-sm" onClick={onClearAll}>
        Clear All
      </Button>
    </Group>
  );
}
